import Button from '2-core-elements/Buttons/Button';
import { type FC, useEffect, useState, useRef } from 'react';

interface MobileFiltersToggleProps {
    onToggle: () => void;
    calendarRef?: React.RefObject<HTMLElement>;
    isOpen: boolean;
    totalResults?: number;
}

const MobileFiltersToggle: FC<MobileFiltersToggleProps> = ({ onToggle, calendarRef, isOpen, totalResults = 0 }) => {
    const [isToggleVisible, setIsToggleVisible] = useState(false);
    const [isAnimatingOut, setIsAnimatingOut] = useState(false);
    const [isCalendarAtTop, setIsCalendarAtTop] = useState(false);
    const [isCalendarAtBottom, setIsCalendarAtBottom] = useState(false);
    const [isContentShorterThanViewport, setIsContentShorterThanViewport] = useState(false);
    const [shouldStickToCalendar, setShouldStickToCalendar] = useState(false);
    const topObserverRef = useRef<IntersectionObserver | null>(null);
    const bottomObserverRef = useRef<IntersectionObserver | null>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const bannerTabsRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        bannerTabsRef.current = document.querySelector('.event-landing-banner-tabs');
    }, []);

    // Observer to detect if calendar content is shorter than viewport
    useEffect(() => {
        if (!calendarRef?.current) {
            return undefined;
        }

        const checkContentHeight = (): void => {
            const calendarElement = calendarRef.current;
            if (!calendarElement) {
                return;
            }

            const calendarHeight = calendarElement.offsetHeight;
            const viewportHeight = window.innerHeight;
            const isShort = calendarHeight < viewportHeight;
            const shouldStick = isShort && totalResults > 0;

            setIsContentShorterThanViewport(isShort);
            setShouldStickToCalendar(shouldStick);

            // Add/remove padding to prevent overlap when sticky
            if (shouldStick) {
                // Add padding to bottom of calendar to prevent toggle overlap
                calendarElement.style.paddingBottom = '80px'; // Approximate height of toggle + padding
            } else {
                // Remove padding when not sticky
                calendarElement.style.paddingBottom = '';
            }
        };

        // Initial check
        checkContentHeight();

        // Set up ResizeObserver to monitor calendar size changes
        resizeObserverRef.current = new ResizeObserver(() => {
            checkContentHeight();
        });

        resizeObserverRef.current.observe(calendarRef.current);

        // Also listen for window resize
        window.addEventListener('resize', checkContentHeight);

        return () => {
            resizeObserverRef.current?.disconnect();
            window.removeEventListener('resize', checkContentHeight);
        };
    }, [calendarRef, totalResults]);

    // Handle banner tabs fixed class based on isOpen state and toggle visibility
    useEffect(() => {
        if (bannerTabsRef.current) {
            // Check if we're on the calendar tab
            const activeTab = document.querySelector('.event-landing-banner-tabs .tab-link.active')?.id;
            const isCalendarTab = activeTab === 'calendar';

            // Only manage banner fixed class when on calendar tab
            if (isCalendarTab) {
                if (isOpen) {
                    bannerTabsRef.current.classList.remove('fixed');
                } else if (isToggleVisible) {
                    // Only add fixed class when toggle is visible and filters are closed
                    bannerTabsRef.current.classList.add('fixed');
                }
            }
            // Don't remove fixed class when toggle is not visible - let EventLandingBanner handle it
        }
    }, [isOpen, isToggleVisible]);

    // Effect to control toggle visibility - show when content is short OR when positioned correctly, but not for 0 results
    useEffect(() => {
        // Don't show toggle if there are 0 results (user can use reset filter CTA instead)
        if (totalResults === 0) {
            if (isToggleVisible) {
                setIsAnimatingOut(true);
                timeoutRef.current = setTimeout(() => {
                    setIsToggleVisible(false);
                    setIsAnimatingOut(false);
                    timeoutRef.current = null;
                }, 300);
            }
            return;
        }

        // Don't show toggle if filters are open
        if (isOpen) {
            if (isToggleVisible) {
                // Immediately hide without animation when filters open
                setIsToggleVisible(false);
                setIsAnimatingOut(false);
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
            }
            return;
        }

        // For short content, always show toggle (will be positioned relative to calendar)
        if (shouldStickToCalendar) {
            if (!isToggleVisible) {
                setIsAnimatingOut(false);
                setIsToggleVisible(true);
            }
            return;
        }

        // For long content, use traditional scroll-based logic
        const shouldShowToggle = isCalendarAtTop && isCalendarAtBottom;
        const shouldHideToggle = (isCalendarAtTop && !isCalendarAtBottom) || !isCalendarAtTop;

        if (shouldShowToggle && !isToggleVisible) {
            // Show toggle
            setIsAnimatingOut(false);
            setIsToggleVisible(true);
        } else if (shouldHideToggle && isToggleVisible) {
            // Hide toggle
            setIsAnimatingOut(true);
            timeoutRef.current = setTimeout(() => {
                setIsToggleVisible(false);
                setIsAnimatingOut(false);
                timeoutRef.current = null;
            }, 300);
        }
    }, [isCalendarAtTop, isCalendarAtBottom, isContentShorterThanViewport, isToggleVisible, totalResults, shouldStickToCalendar, isOpen]);

    useEffect(() => {
        const currentCalendarRef = calendarRef?.current;

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            // Clean up calendar padding on unmount
            if (currentCalendarRef) {
                currentCalendarRef.style.paddingBottom = '';
            }
        };
    }, [calendarRef]);

    // Observer for calendar top edge - detects when calendar reaches top 1% of viewport
    useEffect(() => {
        if (!calendarRef?.current) {
            setIsCalendarAtTop(true);
            return undefined;
        }

        const topObserverOptions = {
            root: null,
            rootMargin: '0px 0px -99% 0px', // Shrink bottom 99% = only detect when element is in top 1% of viewport
            threshold: 0,
        };

        topObserverRef.current = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                setIsCalendarAtTop(entry.isIntersecting);
            });
        }, topObserverOptions);

        topObserverRef.current.observe(calendarRef.current);

        return () => {
            topObserverRef.current?.disconnect();
        };
    }, [calendarRef]);

    // Observer for calendar bottom edge - detects when calendar reaches bottom 1% of viewport
    useEffect(() => {
        if (!calendarRef?.current) {
            setIsCalendarAtBottom(true);
            return undefined;
        }

        const bottomObserverOptions = {
            root: null,
            rootMargin: '-99% 0px 0px 0px', // Shrink top 99% = only detect when element is in bottom 1% of viewport
            threshold: 0,
        };

        bottomObserverRef.current = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                setIsCalendarAtBottom(entry.isIntersecting);
            });
        }, bottomObserverOptions);

        bottomObserverRef.current.observe(calendarRef.current);

        return () => {
            bottomObserverRef.current?.disconnect();
        };
    }, [calendarRef]);

    // Don't render anything if not visible
    if (!isToggleVisible) {
        return null;
    }

    return (
        <div className={`mobile-filters-toggle-wrapper ${isAnimatingOut ? 'fade-out' : ''} ${shouldStickToCalendar ? 'stick-to-calendar' : ''}`}>
            <Button
                skin="1"
                className="mobile-filters-toggle"
                onClick={onToggle}
                type="button"
            >
                Filters
            </Button>
        </div>
    );
};

export default MobileFiltersToggle;
